import { useState, type FormEvent } from 'react'
import { useToast } from 'grabbitbot-legacy-ui'
import { wnfClient } from '../../api'
import { describeTransferError } from '../../lib/errors'
import { decimalGte } from '../../lib/format'

interface Props {
  kind: 'deposit' | 'withdraw'
  coin: string
  /** This coin's minimum from `GET /coins/spot/depositable` (decimal string). */
  minAmount: string
  onDone: () => void
}

/** One of the two test-funds forms of the legacy wallet sheet. */
export function TransferCard({ kind, coin, minAmount, onDone }: Props) {
  const { showToast } = useToast()
  const [amount, setAmount] = useState('')
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isDeposit = kind === 'deposit'
  const title = isDeposit ? 'Deposit test funds' : 'Withdraw test funds'

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (!decimalGte(amount, minAmount)) {
      setError(`Please enter a value which is equal to or greater than ${minAmount}.`)
      return
    }
    setBusy(true)
    try {
      const call = isDeposit ? wnfClient.deposit : wnfClient.withdraw
      await call(coin, amount.trim(), comment)
      setAmount('')
      setComment('')
      showToast(isDeposit ? `Test funds added to ${coin}` : `Test funds taken off ${coin}`, 'success')
      onDone()
    } catch (err) {
      setError(describeTransferError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="wnf-card wnf-transfer" onSubmit={submit} aria-label={title} data-testid={`${kind}-form`}>
      <h2 className={isDeposit ? 'wnf-transfer__title wnf-transfer__title--in' : 'wnf-transfer__title wnf-transfer__title--out'}>
        {title}
      </h2>
      <p className="wnf-muted">Test funds only — nothing here is real money. Minimum {minAmount} {coin}.</p>
      <label>
        Amount ({coin})
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={`min ${minAmount}`}
          aria-label={`${title} amount`}
        />
      </label>
      <label>
        Comment
        <input value={comment} onChange={(e) => setComment(e.target.value)} aria-label={`${title} comment`} />
      </label>
      {error && <p role="alert">{error}</p>}
      <button type="submit" disabled={busy}>
        {busy ? 'Working…' : isDeposit ? 'Deposit' : 'Withdraw'}
      </button>
    </form>
  )
}
